import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Layer, Stage } from 'react-konva'
import QRious from 'qrious'
import JSPDF from 'jspdf'

// Components
import { BadgeGroup } from '../BadgeGroup'
import { BadgeImage } from '../BadgeImage'
import { BadgeList } from '../BadgeList'
import { BadgeRectangle } from '../BadgeRectangle'
import { BadgeText } from '../BadgeText'

// Utils
import { resolveMultiValueLabels, validateLogic } from '../../utils/helpers'
import { resolveListLines } from '../../utils/badgeList'

const MERGE_FIELD = /{{\s(.*?)\s}}/

/**
 *
 * BadgePreview
 *
 */
const BadgePreview = ({
  admissionItems,
  attendee,
  attendees,
  badgeImages,
  categories,
  config,
  endPreview,
  extraBadges,
  kiosk,
  oilTypeOptions,
  sessions,
  useConfirmationCode = false,
}) => {
  // State
  const [attendeeToPrint, setAttendeeToPrint] = useState({ index: 0, attendee: null })
  const [badge, setBadge] = useState(null)
  const [lastBadge, setLastBadge] = useState(false)

  // Refs
  const frontRef = useRef()
  const backRef = useRef()
  const extra1Ref = useRef()
  const extra2Ref = useRef()
  const refs = [frontRef, backRef, extra1Ref, extra2Ref]

  // Timeouts
  const attendeeTimeout = useRef()
  const badgeTimeout = useRef()
  const lastBadgeTimeout = useRef()

  /**
   * Option tables that turn stored ids into the labels logic compares against
   * and lists print. Shared by visibility evaluation and list resolution so the
   * two can't drift apart.
   */
  const logicLookups = useMemo(
    () => ({ categories, admissionItems, sessions, oilTypes: oilTypeOptions }),
    [categories, admissionItems, sessions, oilTypeOptions],
  )

  /**
   * Handles filling in attendee data for text fields.
   * @param {object} attrs
   */
  const resolveAccessorValue = (attendeeDetails, accessor) => {
    // Handle the full name merge field because it requires first and last name
    if (accessor === 'fullName') {
      return `${attendeeDetails.firstName || ''} ${attendeeDetails.lastName || ''}`
    }

    // Manually handle the `presetLocation` merge field since this changes
    // depending on the attendee's country.
    if (accessor === 'presetLocation') {
      // For Non-API Synced Attendees in the US
      if (
        attendeeDetails.country !== undefined &&
        (attendeeDetails.country === 'United States of America' ||
          attendeeDetails.country === 'United States' ||
          attendeeDetails.country === 'US')
      ) {
        // Only concatenate city and state if both are present
        if (attendeeDetails.city && attendeeDetails.state)
          return `${attendeeDetails.city}, ${attendeeDetails.state}`
        return attendeeDetails.city || attendeeDetails.state || ''
      }
      // TODO: remove once we are sure base location works as expected
      // For API Synced Attendees in the US
      if (
        attendeeDetails.customData &&
        attendeeDetails.customData.country !== undefined &&
        (attendeeDetails.customData.country === 'United States of America' ||
          attendeeDetails.customData.country === 'United States' ||
          attendeeDetails.customData?.country === 'US')
      ) {
        // Only concatenate city and state if both are present
        if (attendeeDetails.customData.city && attendeeDetails.customData.state)
          return `${attendeeDetails.customData.city}, ${attendeeDetails.customData.state}`
        return attendeeDetails.customData.city || attendeeDetails.customData.state || ''
      }
      // For Non-API Synced Attendees outside the US
      if (attendeeDetails.country !== undefined) {
        // FIXME: This is a temporary fix for the UK to avoid printing long name
        let formattedCountry = attendeeDetails.country
        if (formattedCountry === 'United Kingdom of Great Britain and Northern Ireland') {
          formattedCountry = 'United Kingdom'
        }

        // Only concatenate city and country if both are present
        if (attendeeDetails.city && formattedCountry)
          return `${attendeeDetails.city}, ${formattedCountry}`
        return attendeeDetails.city || formattedCountry || ''
      }
      // TODO: remove once we are sure base location works as expected
      // For API Synced Attendees outside the US
      if (attendeeDetails.customData && attendeeDetails.customData.country !== undefined) {
        // FIXME: This is a temporary fix for the UK to avoid printing long name
        let formattedCountry = attendeeDetails.customData.country
        if (formattedCountry === 'United Kingdom of Great Britain and Northern Ireland') {
          formattedCountry = 'United Kingdom'
        }

        // Only concatenate city and country if both are present
        if (attendeeDetails.customData.city && formattedCountry)
          return `${attendeeDetails.customData.city}, ${formattedCountry}`
        return attendeeDetails.customData.city || formattedCountry || ''
      }
      return ''
    }

    // Resolve admission item ID to its display name
    if (accessor === 'admissionItem') {
      const itemId = attendeeDetails.admissionItem
      if (itemId && admissionItems && admissionItems[itemId]) {
        return admissionItems[itemId].name
      }
      return ''
    }

    // Only pull from `customData` if the `accessor` isn't valid for the base attendee
    // Get the merge field accessor and verify that the attendee has `customData`
    if (
      (attendeeDetails[accessor] === undefined || !attendeeDetails[accessor]) &&
      attendeeDetails.customData &&
      attendeeDetails.customData[accessor] !== undefined
    ) {
      const val = attendeeDetails.customData[accessor]
      return _.isArray(val) ? val.join(', ') : `${val || ''}`
    }

    // Only pull from `customData` if the `accessor` isn't valid for the base attendee
    // If the attendee does not have this custom data (e.g Cvent doesn't return it if its not set)
    // then we should just replace the merge field with an empty string.
    if (
      (attendeeDetails[accessor] === undefined || !attendeeDetails[accessor]) &&
      attendeeDetails.customData &&
      attendeeDetails.customData[accessor] === undefined
    ) {
      return ''
    }

    // Otherwise, use the base attendee data
    return `${attendeeDetails[accessor] || ''}`
  }

  const updateTextField = (attendeeDetails, attrs) => {
    const updatedAttrs = attrs

    // Check if the text field has a merge field
    if (MERGE_FIELD.test(updatedAttrs.text)) {
      // Update the text field with the resolved merge field, removing escaped quotes
      updatedAttrs.text = resolveAccessorValue(attendeeDetails, updatedAttrs.accessor).replaceAll(
        /[\\"]/g,
        '',
      )
    }

    return updatedAttrs
  }

  /**
   * Resolves the merge-field tokens in a custom QR code's content against the
   * attendee. Literal text is preserved; each `{{ accessor }}` token is replaced
   * with the attendee's resolved value.
   * @param {object} attendeeDetails
   * @param {string} content
   * @returns {string} resolved content
   */
  const resolveQrContent = (attendeeDetails, content) => {
    if (!content) return ''

    return content.replace(/{{\s*(.*?)\s*}}/g, (fullMatch, accessor) =>
      resolveAccessorValue(attendeeDetails, accessor.trim()).replaceAll(/[\\"]/g, ''),
    )
  }

  /**
   * Resolves a list object's configured lines against the attendee.
   *
   * The surviving rows are stashed on `attrs.resolvedLines` so the renderer
   * draws exactly what should print — lines whose logic fails, whose merge
   * fields all resolve empty, or whose repeat field holds nothing never reach
   * it, which is what makes them collapse instead of reserving blank space.
   * @param {object} attendeeDetails
   * @param {object} element list element (mutated in place)
   */
  const resolveList = (attendeeDetails, element) => {
    const updatedElement = element

    updatedElement.attrs = {
      ...updatedElement.attrs,
      resolvedLines: resolveListLines(updatedElement.attrs, attendeeDetails, {
        lookups: logicLookups,
        resolveAccessor: (accessor) => resolveAccessorValue(attendeeDetails, accessor),
        resolveListValues: (field) =>
          resolveMultiValueLabels(field, attendeeDetails, logicLookups),
      }),
    }
  }

  /**
   * Parse elements and update text fields with attendee data.
   * @param {object} element
   */
  const parseElement = (attendeeDetails, element) => {
    const updatedElement = element

    // Fill in text fields with attendee data
    if (updatedElement.className === 'Text') {
      updatedElement.attrs = updateTextField(attendeeDetails, updatedElement.attrs)
    }
    // Resolve the lines of a list object against this attendee
    else if (updatedElement.className === 'List') {
      resolveList(attendeeDetails, updatedElement)
    }
    // Custom QR codes encode organizer-authored content (with merge fields
    // resolved) and always render, regardless of GDPR opt-in / quick badge.
    else if (updatedElement.className === 'Image' && updatedElement.attrs.customQr) {
      const qr = new QRious({
        size: updatedElement.attrs.width,
        value: resolveQrContent(attendeeDetails, updatedElement.attrs.qrContent),
      })

      updatedElement.attrs.src = qr.toDataURL()
    }
    // If the element is a QR code, generate the QR code using the attendee id
    // Don't generate if the attendee hasn't opted into GDPR or if the badge is a quick badge
    else if (
      updatedElement.className === 'Image' &&
      updatedElement.attrs.qrImage &&
      attendeeDetails.gdprOptIn &&
      !attendeeDetails.quickBadge
    ) {
      const qr = new QRious({
        size: updatedElement.attrs.width,
        value: useConfirmationCode ? attendeeDetails.confirmationCode : attendeeDetails.id,
      })

      updatedElement.attrs.src = qr.toDataURL()
    }
    // Fill in text fields with attendee data for groups
    else if (updatedElement.className === 'Group') {
      updatedElement.children = _.map(updatedElement.children, (child) => {
        const updatedChild = child

        if (updatedChild.className === 'Text') {
          updatedChild.attrs = updateTextField(attendeeDetails, updatedChild.attrs)
        } else if (updatedChild.className === 'List') {
          resolveList(attendeeDetails, updatedChild)
        } else if (updatedChild.className === 'Image' && updatedChild.attrs.customQr) {
          const qr = new QRious({
            size: updatedChild.attrs.width,
            value: resolveQrContent(attendeeDetails, updatedChild.attrs.qrContent),
          })

          updatedChild.attrs.src = qr.toDataURL()
        } else if (updatedElement.className === 'Image' && updatedElement.attrs.qrImage) {
          const qr = new QRious({
            size: updatedElement.attrs.width,
            value: useConfirmationCode ? attendeeDetails.confirmationCode : attendeeDetails.id,
          })

          updatedElement.attrs.src = qr.toDataURL()
        }

        return updatedChild
      })
    }
  }

  /**
   * Generate a badge for the specified `attendee`.
   * @param {object} attendee
   */
  const generateBadgeForAttendee = (attendeeDetails) => {
    // Generate badge config for printing
    const badgeConfig = { 1: _.cloneDeep(config.configObject[1]) }

    // If there are more than 1 panels, add the second panel (mirrored if enabled)
    if (config.numPanels > 1) {
      badgeConfig['2'] = _.cloneDeep(config.configObject[`${config.enableMirrorContent ? 1 : 2}`])

      // If there are more than 2 panels, add the remaining panels
      if (config.numPanels > 2) {
        // Add remaining panels
        _.forEach(_.range(3, config.numPanels + 1), (i) => {
          badgeConfig[`${i}`] = _.cloneDeep(config.configObject[`${i}`])
        })
      }
    }

    // Loop through badge config and replace any merge fields with attendee data
    _.forEach(_.keys(badgeConfig), (panelIndex) => {
      const panel = badgeConfig[panelIndex]
      _.forEach(panel.children, (layer) => {
        // If the layer has children, loop through them
        if (layer.children.length > 0) {
          _.forEach(layer.children, (child) => {
            let updatedChild = child
            // If the child is a `Group`, then we need to loop through those children
            if (child.class_name === 'Group') {
              updatedChild.children = _.map(updatedChild.children, (groupChild) =>
                parseElement(attendeeDetails, groupChild),
              )
            } else {
              updatedChild = parseElement(attendeeDetails, child)
            }
          })
        }
      })
    })

    setBadge(badgeConfig)
  }

  /**
   * Create a new instance of JSPDF.
   */
  const pdf = useMemo(() => {
    if ((attendees || attendee) && config) {
      // Calculate the height of the pdf
      const count = attendees ? attendees.length : 1 + (Number(extraBadges) || 0)
      const height =
        config.configObject[1].attrs.height * 2 * _.keys(config.configObject).length * count
      const width = config.configObject[1].attrs.width * 2
      const orientation = width > height ? 'l' : 'p'

      return new JSPDF({
        orientation,
        unit: 'px',
        format: [width, height],
        hotfixes: ['px_scaling'],
      })
    }

    return null
  }, [attendees, attendee, config])

  /**
   * Start badge generation for the specified `attendee`.
   */
  useEffect(() => {
    if (attendeeToPrint.attendee) {
      generateBadgeForAttendee(attendeeToPrint.attendee)
    }
  }, [attendeeToPrint])

  /**
   * Start badge generation for the specified `attendee` or bulk generate
   * for the specified `attendees`.
   */
  useEffect(() => {
    if (attendee) {
      setAttendeeToPrint({ ...attendeeToPrint, attendee })
    } else if (attendees) {
      // Loop through each attendee and generate a badge for each
      _.forEach(attendees, (attendeeDetails, i) => {
        // Generate badge for each attendee, waiting in between
        attendeeTimeout.current = setTimeout(() => {
          setAttendeeToPrint({ index: i, attendee: attendeeDetails })
        }, 800 * (i + 1))
      })
    }
  }, [attendee, attendees])

  /**
   * When bulk printing, open the pdf in a new tab after the last badge is generated.
   */
  useEffect(() => {
    if (lastBadge) {
      lastBadgeTimeout.current = setTimeout(() => {
        // Open the pdf in a new tab
        pdf.autoPrint()
        const blob = pdf.output('bloburl')

        // If we are previewing the badge, open the pdf in a new tab
        if (!kiosk) {
          window.open(blob)

          // Reset everything and end the preview
          setBadge(null)
          setAttendeeToPrint({ index: 0, attendee: null })
          setLastBadge(false)

          // End the preview
          endPreview()
        }
        // Otherwise open print dialog with the file
        else {
          const hiddenFrame = document.createElement('iframe')
          hiddenFrame.style.position = 'fixed'
          hiddenFrame.style.width = '1px'
          hiddenFrame.style.height = '1px'
          hiddenFrame.style.opacity = '0.01'
          const isSafari = /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent)
          if (isSafari) {
            // fallback in safari
            hiddenFrame.onload = () => {
              try {
                hiddenFrame.contentWindow.document.execCommand('print', false, null)
              } catch (e) {
                hiddenFrame.contentWindow.print()
              }
            }
          }
          hiddenFrame.src = blob
          document.body.appendChild(hiddenFrame)

          setTimeout(() => {
            document.body.removeChild(hiddenFrame)

            // Reset everything and end the preview
            setBadge(null)
            setAttendeeToPrint({ index: 0, attendee: null })
            setLastBadge(false)

            // End the preview
            endPreview()
          }, 3000) // 3 seconds
        }
      }, 500)
    }

    return () => {
      if (attendeeTimeout.current) clearTimeout(attendeeTimeout.current)
      if (badgeTimeout.current) clearTimeout(badgeTimeout.current)
      if (lastBadgeTimeout.current) clearTimeout(lastBadgeTimeout.current)
    }
  }, [lastBadge])

  /**
   * Captures badge panel images from Konva stages after ensuring fonts are rendered.
   * Waits for document.fonts.ready, then forces Konva to re-draw so canvas text uses
   * the loaded fonts (canvas doesn't auto-update when fonts become available).
   */
  const captureBadgeImages = async () => {
    // Ensure any in-progress font loads are complete before capturing
    await document.fonts.ready

    // Force Konva stages to re-draw with the now-loaded fonts — canvas text rendering
    // doesn't automatically update when web fonts become available after initial draw
    _.forEach(refs, (ref) => {
      if (ref.current) {
        ref.current.batchDraw()
      }
    })

    // Wait for the re-draw to complete
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))

    const updatedBadgeImages = []
    _.forEach(refs, (ref) => {
      if (ref.current) {
        const dataUrl = ref.current.toDataURL({ pixelRatio: 2 })
        updatedBadgeImages.push(dataUrl)
      }
    })
    return updatedBadgeImages
  }

  useEffect(() => {
    if (badge) {
      if (!attendees) {
        const capture = async () => {
          const updatedBadgeImages = await captureBadgeImages()

          // Add each panel as an image to the pdf, for each number of badges to print
          const numBadgesToPrint = 1 + (Number(extraBadges) || 0)
          _.forEach(_.range(numBadgesToPrint), (index) => {
            _.forEach(updatedBadgeImages, (image, badgeIndex) => {
              const adjustment = index * 2 + badgeIndex
              pdf.addImage(
                image,
                0,
                badge[1].attrs.height * 2 * adjustment,
                badge[1].attrs.width * 2,
                badge[1].attrs.height * 2,
              )
            })
          })

          // Open the pdf in a new tab
          pdf.autoPrint()
          const blob = pdf.output('bloburl')

          // If we are previewing the badge, open the pdf in a new tab
          if (!kiosk) {
            window.open(blob)

            // Reset everything and end the preview
            setBadge(null)
            setAttendeeToPrint({ index: 0, attendee: null })
            setLastBadge(false)

            // End the preview
            endPreview()
          }
          // Otherwise open print dialog with the file
          else {
            const hiddenFrame = document.createElement('iframe')
            hiddenFrame.style.position = 'fixed'
            hiddenFrame.style.width = '1px'
            hiddenFrame.style.height = '1px'
            hiddenFrame.style.opacity = '0.01'
            const isSafari = /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent)
            if (isSafari) {
              // fallback in safari
              hiddenFrame.onload = () => {
                try {
                  hiddenFrame.contentWindow.document.execCommand('print', false, null)
                } catch (e) {
                  hiddenFrame.contentWindow.print()
                }
              }
            }
            hiddenFrame.src = blob
            document.body.appendChild(hiddenFrame)

            setTimeout(() => {
              document.body.removeChild(hiddenFrame)

              // Reset everything and end the preview
              setBadge(null)
              setAttendeeToPrint({ index: 0, attendee: null })
              setLastBadge(false)

              // End the preview
              endPreview()
            }, 3000) // 3 seconds
          }

          // Clear the badge and attendee
          setBadge(null)
          setAttendeeToPrint({ index: 0, attendee: null })

          // End the preview
          endPreview()
        }

        capture()
      } else {
        const capture = async () => {
          const updatedBadgeImages = await captureBadgeImages()
          const updatedHeight = badge[1].attrs.height * 2

          // Calculate the y offset for the images
          let y = 0
          if (attendeeToPrint.index > 0) {
            // Multiple the height of each badge image by 2, the number of panels, and the number of attendees added
            // to get the y offset for the next badge
            y = updatedHeight * updatedBadgeImages.length * attendeeToPrint.index
          }

          // Add each panel as an image to the pdf
          _.forEach(updatedBadgeImages, (image) => {
            pdf.addImage(image, 0, y, badge[1].attrs.width * 2, updatedHeight)

            // Increment the y offset for the next badge
            y += updatedHeight
          })

          // If this is the last badge, trigger the print
          if (attendeeToPrint.index === attendees.length - 1) {
            setLastBadge(true)
          }

          // Reset badge refs
          frontRef.current = null
          backRef.current = null
          extra1Ref.current = null
          extra2Ref.current = null

          // Clear the badge and attendee
          setBadge(null)
          setAttendeeToPrint({ index: 0, attendee: null })
        }

        capture()
      }
    }

    return () => {
      if (!attendees) {
        if (attendeeTimeout.current) clearTimeout(attendeeTimeout.current)
        if (badgeTimeout.current) clearTimeout(badgeTimeout.current)
        if (lastBadgeTimeout.current) clearTimeout(lastBadgeTimeout.current)
      }
    }
  }, [badge])

  const checkVisibilityLogic = (element) => {
    let visibilityLogic = element.attrs.visibility

    // If `visibilityLogic` is the old format, convert to logic array
    if (_.isString(visibilityLogic)) {
      const [field, operator, value] = element.attrs.visibility.split(':')
      visibilityLogic = [
        {
          inclusionLogic: 'AND',
          mergeField: field,
          operator,
          value,
        },
      ]
    }

    return validateLogic(visibilityLogic, attendeeToPrint.attendee, logicLookups)
  }

  /**
   * Configures the visibility for the specified `element`.
   * - Layers can only be configured visible for a specific category.
   * - All other elements can be configured based on a custom merge field value.
   *   - Visibility for an element is stored as `<field>:<operator>:<value>`.
   *   - Visbility can be based on equality or contains.
   *   - Sessions must be verified by the session id.
   * @param {object} element
   * @returns {bool} whether to show the element or not
   */
  const configureVisibility = (element) => {
    // `base` and `always` elements are always visible, but not qr codes.
    // Custom QR codes are exempt from the registration-QR GDPR gating below —
    // they carry organizer-authored content and always render.
    if (
      (element.attrs.id === 'base' || element.attrs.visibility === 'always') &&
      (!element.attrs.qrImage || element.attrs.customQr)
    )
      return true

    // Check if the element is a QR code and should be displayed
    if (
      element.className === 'Image' &&
      element.attrs.qrImage &&
      // Only if the QR code should alwasy be visible
      element.attrs.visibility === 'always' &&
      // If the attendee opted in to GDPR or the QR code is scrambled
      (attendeeToPrint.attendee.gdprOptIn || element.attrs.qrPrint === 'scrambled') &&
      // If the badge is not a quick badge
      !attendeeToPrint.attendee.quickBadge
    )
      return true
    // Check layer visibility. If the layer is marked for a specific category,
    // we need to make sure this badge is for that category.
    // Only applies to the old string format (e.g. "categoryId:is:Speaker").
    // New array format is handled by checkVisibilityLogic below.
    if (element.className === 'Layer' && categories && _.isString(element.attrs.visibility)) {
      // Pull out the category this layer is visible for (uses category id)
      const category = element.attrs.visibility.split(':')[2]

      // Only display if the attendee has this category
      if (attendeeToPrint.attendee.categoryId === category) {
        return true
      }
    }

    // Check element visibility. These are based on custom merge field values.
    if (element.attrs.id !== 'base' && element.attrs.visibility !== 'always') {
      const isVisible = checkVisibilityLogic(element)

      // Check if the element is a QR code and should not be printed based
      // on the attendee's GDPR opt-in status or if the badge is a quick badge
      if (
        isVisible &&
        element.className === 'Image' &&
        element.attrs.qrImage &&
        element.attrs.qrPrint === 'no-print' &&
        !attendeeToPrint.attendee.gdprOptIn &&
        !attendeeToPrint.attendee.quickBadge
      ) {
        return false
      }

      return isVisible
    }

    // Default to false if the visibility conditions are not met
    return false
  }

  /**
   * Renders child elements.
   * @param {object} child child element to render
   * @param {string} transformerId id of the transformer
   * @returns
   */
  const renderChild = (child, transformerId) => {
    const visible = configureVisibility(child)

    let base64 = null
    if (kiosk && child.className === 'Image' && !child.attrs.qrImage) {
      // Find the matching image from the `badgeImages`
      const match = _.find(badgeImages, (i) => child.attrs.badgeImagePath.includes(i.id))
      if (match) {
        base64 = match.base64
      }
    } else {
      base64 = _.has(badgeImages, child.attrs.id) ? badgeImages[child.attrs.id]?.base64 : null
    }

    switch (child.className) {
      case 'Image':
        return (
          <BadgeImage
            base64={base64}
            data={child}
            draggable={false}
            key={child.attrs.id}
            onChange={() => {}}
            transformerId={transformerId}
            visible={visible}
          />
        )
      case 'Rect':
        return (
          <BadgeRectangle
            data={child}
            draggable={false}
            key={child.attrs.id}
            onChange={() => {}}
            transformerId={transformerId}
            visible={visible}
          />
        )
      case 'Text':
        return (
          <BadgeText
            autoFontSize
            data={child}
            draggable={false}
            editable={false}
            font={config.defaultGoogleFont}
            key={child.attrs.id}
            onChange={() => {}}
            transformerId={transformerId}
            visible={visible}
          />
        )
      case 'Group':
        return (
          <BadgeGroup
            autoFontSize
            badgeImages={badgeImages}
            data={child}
            defaultFont={config.defaultGoogleFont}
            key={child.attrs.id}
            kiosk={kiosk}
            onChange={() => {}}
            transformerId={transformerId}
            visible={visible}
          />
        )
      case 'List':
        return (
          <BadgeList
            data={child}
            draggable={false}
            key={child.attrs.id}
            onChange={() => {}}
            transformerId={transformerId}
            visible={visible}
          />
        )
      default:
        return null
    }
  }

  return badge ? (
    <div className="hidden">
      {_.map(_.keys(badge), (panelIndex) => (
        <Stage
          id={badge[panelIndex].attrs.id}
          key={badge[panelIndex].attrs.id}
          height={badge[panelIndex].attrs.height}
          offsetX={panelIndex === '2' ? badge[panelIndex].attrs.width : 0}
          offsetY={panelIndex === '2' ? badge[panelIndex].attrs.height : 0}
          ref={refs[panelIndex - 1]}
          scaleY={panelIndex === '2' ? -1 : 1}
          scaleX={panelIndex === '2' ? -1 : 1}
          width={badge[panelIndex].attrs.width}
        >
          {/* All other layers */}
          {_.map(badge[panelIndex].children, (layer, index) => (
            <Layer
              visible={configureVisibility(layer)}
              id={layer.attrs.id}
              key={layer.attrs.id}
              pixelRatio={2}
            >
              {_.map(layer.children, (child) => renderChild(child, `layer:${index}`))}
            </Layer>
          ))}
        </Stage>
      ))}
    </div>
  ) : null
}

BadgePreview.defaultProps = {
  admissionItems: null,
  attendee: null,
  attendees: null,
  badgeImages: null,
  categories: null,
  endPreview: () => {},
  extraBadges: 0,
  kiosk: false,
  oilTypeOptions: null,
  sessions: null,
}

BadgePreview.propTypes = {
  admissionItems: PropTypes.object,
  attendee: PropTypes.object,
  attendees: PropTypes.array,
  badgeImages: PropTypes.array,
  categories: PropTypes.object,
  config: PropTypes.object.isRequired,
  endPreview: PropTypes.func,
  extraBadges: PropTypes.number,
  kiosk: PropTypes.bool,
  oilTypeOptions: PropTypes.object,
  sessions: PropTypes.object,
  useConfirmationCode: PropTypes.bool,
}

export default BadgePreview
